import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenantBranding } from "../../../shared/branding/TenantBrandingProvider";
import appConfig from "../../../config/appConfig";
import styles from "./mainLanding.module.css";
import logoSymbol from "../../../images/logoSymbol.png";
import faceLogo from "../../../images/face.png";

const DEMO_PATH = appConfig.DEMO_PATH;

const trustItemKeys = [
  "landing_trust_telegram_requests",
  "landing_trust_setup_included",
  "landing_trust_built_for_small_teams",
];

const fitItemKeys = [
  {
    title: "landing_fit_groomers_title",
    text: "landing_fit_groomers_text",
  },
  {
    title: "landing_fit_flower_shops_title",
    text: "landing_fit_flower_shops_text",
  },
  {
    title: "landing_fit_confectioners_title",
    text: "landing_fit_confectioners_text",
  },
  {
    title: "landing_fit_realtors_title",
    text: "landing_fit_realtors_text",
  },
  {
    title: "landing_fit_beauty_title",
    text: "landing_fit_beauty_text",
  },
  {
    title: "landing_fit_delivery_title",
    text: "landing_fit_delivery_text",
  },
];

const HERO_TITLE_LINES = [
  [{ key: "landing_hero_line1_part1", accent: false },
  { key: "landing_hero_line2_part1", accent: true }
  ],
  [

    { key: "landing_hero_line2_part2", accent: false },
  ],
  [{ key: "landing_hero_line3_part1", accent: true }],
  [{ key: "landing_hero_line4_part1", accent: true }],
];

const initialForm = {
  email: "",
  message: "",
  captcha_token: "",
};

function TurnstileWidget({ onVerify, onExpire, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    const SCRIPT_ID = "cf-turnstile-script";
    const SITE_KEY = appConfig.SITE_KEY;

    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current !== null) return;

      if (!SITE_KEY || typeof SITE_KEY !== "string") {
        onError();
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => onVerify(token),
        "expired-callback": () => onExpire(),
        "error-callback": () => onError(),
        theme: "light",
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    let script = document.getElementById(SCRIPT_ID);

    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    } else {
      script.addEventListener("load", renderWidget);
    }

    return () => {
      if (script) {
        script.removeEventListener("load", renderWidget);
      }

      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch { }
        widgetIdRef.current = null;
      }
    };
  }, [onVerify, onExpire, onError]);

  return <div ref={containerRef} className={styles.turnstileBox} />;
}

function SectionCards({ items }) {
  return (
    <div className={styles.featureGrid}>
      {items.map((item) => (
        <article key={item.title} className={styles.featureCard}>
          <span className={styles.featureEyebrow}>{item.eyebrow}</span>
          <h3 className={styles.featureTitle}>{item.title}</h3>
          <p className={styles.featureText}>{item.text}</p>
        </article>
      ))}
    </div>
  );
}

function AnimatedHeroTitle() {
  const { t } = useTranslation();

  const typingSpeed = 62;
  const deletingSpeed = 38;
  const pauseAfterTyped = 1400;
  const pauseAfterDeleted = 320;

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const translatedLines = useMemo(() => {
    return HERO_TITLE_LINES.map((line) =>
      line.map((segment) => {
        const text = t(segment.key);
        return {
          ...segment,
          text,
          length: text.length,
        };
      })
    );
  }, [t]);

  const flatSegments = useMemo(() => {
    return translatedLines.flatMap((line, lineIndex) =>
      line.map((segment) => ({
        ...segment,
        lineIndex,
      }))
    );
  }, [translatedLines]);

  const totalChars = useMemo(() => {
    return flatSegments.reduce((sum, segment) => sum + segment.length, 0);
  }, [flatSegments]);

  const [visibleChars, setVisibleChars] = useState(
    prefersReducedMotion ? totalChars : 0
  );
  const [phase, setPhase] = useState(prefersReducedMotion ? "done" : "typing");

  useEffect(() => {
    if (prefersReducedMotion) return;

    let timeoutId;

    if (phase === "typing") {
      if (visibleChars < totalChars) {
        timeoutId = window.setTimeout(() => {
          setVisibleChars((prev) => prev + 1);
        }, typingSpeed);
      } else {
        timeoutId = window.setTimeout(() => {
          setPhase("pause-full");
        }, pauseAfterTyped);
      }
    } else if (phase === "pause-full") {
      timeoutId = window.setTimeout(() => {
        setPhase("deleting");
      }, pauseAfterTyped);
    } else if (phase === "deleting") {
      if (visibleChars > 0) {
        timeoutId = window.setTimeout(() => {
          setVisibleChars((prev) => prev - 1);
        }, deletingSpeed);
      } else {
        timeoutId = window.setTimeout(() => {
          setPhase("pause-empty");
        }, pauseAfterDeleted);
      }
    } else if (phase === "pause-empty") {
      timeoutId = window.setTimeout(() => {
        setPhase("typing");
      }, pauseAfterDeleted);
    }

    return () => window.clearTimeout(timeoutId);
  }, [
    phase,
    visibleChars,
    totalChars,
    typingSpeed,
    deletingSpeed,
    pauseAfterTyped,
    pauseAfterDeleted,
    prefersReducedMotion,
  ]);

  const lineData = useMemo(() => {
    let remaining = visibleChars;

    return translatedLines.map((line, lineIndex) => {
      const renderedSegments = line.map((segment) => {
        const visibleCount = Math.max(0, Math.min(segment.text.length, remaining));
        remaining -= visibleCount;

        return {
          ...segment,
          visibleText: segment.text.slice(0, visibleCount),
          isComplete: visibleCount === segment.text.length,
          visibleCount,
        };
      });

      const lineTextLength = renderedSegments.reduce(
        (sum, segment) => sum + segment.visibleText.length,
        0
      );

      return {
        lineIndex,
        segments: renderedSegments,
        visibleLength: lineTextLength,
      };
    });
  }, [translatedLines, visibleChars]);

  const activeLineIndex = useMemo(() => {
    for (let i = lineData.length - 1; i >= 0; i -= 1) {
      if (lineData[i].visibleLength > 0) return i;
    }
    return 0;
  }, [lineData]);

  return (
    <h1
      className={styles.heroTitle}
      aria-label={t("landing_hero_aria_label")}
    >
      {lineData.map((line, index) => {
        const isActiveLine = index === activeLineIndex;
        const isLastVisibleLine = isActiveLine;

        return (
          <span key={index} className={styles.typeLine}>
            <span className={styles.typeLineInner}>
              {line.segments.map((segment, segmentIndex) => {
                if (!segment.visibleText) return null;

                return (
                  <span
                    key={`${index}-${segmentIndex}`}
                    className={segment.accent ? styles.typeAccent : undefined}
                  >
                    {segment.visibleText}
                  </span>
                );
              })}

              {isLastVisibleLine && (
                <span className={styles.typeCursor} aria-hidden="true" />
              )}
            </span>
          </span>
        );
      })}
    </h1>
  );
}

function CountUp({ to, suffix = "", duration = 1400, decimals = 0 }) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let frameId;
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = to * eased;
      setValue(current);

      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      }
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [to, duration]);

  const formatted =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();

  return (
    <span>
      {formatted}
      {suffix}
    </span>
  );
}

export default function MainLandingPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t("site_title");
  }, [t]);

  const { branding } = useTenantBranding();
  const companyName = branding?.company_name || "Vel Soft CRM";

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(initialForm);

  const trustItems = useMemo(() => trustItemKeys.map((key) => t(key)), [t]);

  const fitItems = useMemo(
    () =>
      fitItemKeys.map((item) => ({
        title: t(item.title),
        text: t(item.text),
      })),
    [t]
  );

  const workflowItems = useMemo(
    () => [
      {
        number: "01",
        title: t("landing_workflow_step_1_title"),
        text: t("landing_workflow_step_1_text"),
      },
      {
        number: "02",
        title: t("landing_workflow_step_2_title"),
        text: t("landing_workflow_step_2_text"),
      },
      {
        number: "03",
        title: t("landing_workflow_step_3_title"),
        text: t("landing_workflow_step_3_text"),
      },
      {
        number: "04",
        title: t("landing_workflow_step_4_title"),
        text: t("landing_workflow_step_4_text"),
      },
    ],
    [t]
  );

  const telegramItems = useMemo(
    () => [
      t("landing_telegram_point_1"),
      t("landing_telegram_point_2"),
      t("landing_telegram_point_3"),
      t("landing_telegram_point_4"),
      t("landing_telegram_point_5"),
    ],
    [t]
  );

  const featureItems = useMemo(
    () => [
      {
        eyebrow: t("landing_feature_visibility_eyebrow"),
        title: t("landing_feature_visibility_title"),
        text: t("landing_feature_visibility_text"),
      },
      {
        eyebrow: t("landing_feature_consistency_eyebrow"),
        title: t("landing_feature_consistency_title"),
        text: t("landing_feature_consistency_text"),
      },
      {
        eyebrow: t("landing_feature_scalability_eyebrow"),
        title: t("landing_feature_scalability_title"),
        text: t("landing_feature_scalability_text"),
      },
    ],
    [t]
  );

  const plans = useMemo(
    () => [
      {
        name: t("landing_plan_starter_name"),
        price: "€29",
        period: t("landing_plan_month_period"),
        description: t("landing_plan_starter_description"),
        setup: t("landing_plan_starter_setup"),
        points: [
          t("landing_plan_starter_point_1"),
          t("landing_plan_starter_point_2"),
          t("landing_plan_starter_point_3"),
          t("landing_plan_starter_point_4"),
          t("landing_plan_starter_point_5"),
        ],
        tone: "light",
      },
      {
        name: t("landing_plan_growth_name"),
        price: "€49",
        period: t("landing_plan_month_period"),
        description: t("landing_plan_growth_description"),
        setup: t("landing_plan_growth_setup"),
        points: [
          t("landing_plan_growth_point_1"),
          t("landing_plan_growth_point_2"),
          t("landing_plan_growth_point_3"),
          t("landing_plan_growth_point_4"),
          t("landing_plan_growth_point_5"),
          t("landing_plan_growth_point_6"),
        ],
        tone: "primary",
        badge: t("landing_plan_popular_badge"),
      },
      {
        name: t("landing_plan_pro_name"),
        price: "€79",
        period: t("landing_plan_month_period"),
        description: t("landing_plan_pro_description"),
        setup: t("landing_plan_pro_setup"),
        points: [
          t("landing_plan_pro_point_1"),
          t("landing_plan_pro_point_2"),
          t("landing_plan_pro_point_3"),
          t("landing_plan_pro_point_4"),
          t("landing_plan_pro_point_5"),
        ],
        tone: "dark",
      },
    ],
    [t]
  );

  const validate = useMemo(
    () => () => {
      if (!form.email.trim()) return t("landing_form_error_email_required");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        return t("landing_form_error_email_invalid");
      }
      if (!form.message.trim()) return t("landing_form_error_message_required");
      if (!form.captcha_token.trim()) return t("landing_form_error_captcha_required");
      return "";
    },
    [form, t]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch(`${appConfig.API_BASE_URL}/public/lead-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: form.email.trim(),
          message: form.message.trim(),
          captcha_token: form.captcha_token,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || t("landing_form_error_submit"));
      }

      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      setError(err?.message || t("landing_form_error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <span className={styles.pageAuraOne} />
      <span className={styles.pageAuraTwo} />
      <span className={styles.pageAuraThree} />
      <span className={styles.heroChatDecor} aria-hidden="true"><i class="lni lni-message-2"></i></span>
      <span className={styles.heroBotDecor} aria-hidden="true"><svg width="70" height="70" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
        <path d="M7.91274 11.9393C8.71274 11.3393 9.81274 11.3393 10.6127 11.9393L10.7127 12.0143C11.0441 12.2628 11.1113 12.7329 10.8627 13.0643C10.6142 13.3956 10.1441 13.4628 9.81274 13.2143L9.71274 13.1393C9.44607 12.9393 9.07941 12.9393 8.81274 13.1393L8.71274 13.2143C8.38137 13.4628 7.91127 13.3956 7.66274 13.0643C7.41421 12.7329 7.48137 12.2628 7.81274 12.0143L7.91274 11.9393Z" fill="#343C54"></path>
        <path d="M16.1127 11.9393C15.3127 11.3393 14.2127 11.3393 13.4127 11.9393L13.3127 12.0143C12.9814 12.2628 12.9142 12.7329 13.1627 13.0643C13.4113 13.3956 13.8814 13.4628 14.2127 13.2143L14.3127 13.1393C14.5794 12.9393 14.9461 12.9393 15.2127 13.1393L15.3127 13.2143C15.6441 13.4628 16.1142 13.3956 16.3627 13.0643C16.6113 12.7329 16.5441 12.2628 16.2127 12.0143L16.1127 11.9393Z" fill="#343C54"></path>
        <path d="M11.2081 15.583C11.2081 15.1688 10.8723 14.833 10.4581 14.833C10.0438 14.833 9.70805 15.1688 9.70805 15.583C9.70805 16.856 10.74 17.888 12.0131 17.888C13.2861 17.888 14.3181 16.856 14.3181 15.583C14.3181 15.1688 13.9823 14.833 13.5681 14.833C13.1538 14.833 12.8181 15.1688 12.8181 15.583C12.8181 16.0276 12.4576 16.388 12.0131 16.388C11.5685 16.388 11.2081 16.0276 11.2081 15.583Z" fill="#343C54"></path>
        <path d="M9.69531 3.555C9.69531 4.56533 10.3453 5.42384 11.25 5.73513V7.35938H5.75C4.50736 7.35938 3.5 8.36673 3.5 9.60938V11.4327C2.62611 11.7416 2 12.575 2 13.5547V14.5547C2 15.5344 2.62611 16.3678 3.5 16.6767V18.6094C3.5 19.852 4.50736 20.8594 5.75 20.8594H18.25C19.4926 20.8594 20.5 19.852 20.5 18.6094V16.6767C21.3739 16.3678 22 15.5344 22 14.5547V13.5547C22 12.575 21.3739 11.7416 20.5 11.4327V9.60938C20.5 8.36673 19.4926 7.35938 18.25 7.35938H12.75V5.73534C13.655 5.42424 14.3053 4.56557 14.3053 3.555C14.3053 2.28198 13.2733 1.25 12.0003 1.25C10.7273 1.25 9.69531 2.28198 9.69531 3.555ZM12.0003 2.75C12.4449 2.75 12.8053 3.11041 12.8053 3.555C12.8053 3.99221 12.4568 4.348 12.0224 4.3597C12.015 4.35948 12.0075 4.35938 12 4.35938C11.9926 4.35938 11.9853 4.35948 11.978 4.3597C11.5437 4.34786 11.1953 3.99212 11.1953 3.555C11.1953 3.11041 11.5557 2.75 12.0003 2.75ZM19 9.60938V18.6094C19 19.0236 18.6642 19.3594 18.25 19.3594H5.75C5.33579 19.3594 5 19.0236 5 18.6094V9.60938C5 9.19516 5.33579 8.85938 5.75 8.85938H18.25C18.6642 8.85938 19 9.19516 19 9.60938Z" fill="#343C54"></path>
      </svg></span>
      <span className={styles.heroChartDecor} aria-hidden="true"><i class="lni lni-bar-chart-4"></i></span>

      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          <div className={styles.logoWrapper}>
            <img src={logoSymbol} alt={t("landing_logo_alt")} className={styles.logoIcon} />
          </div>

          <nav className={styles.nav}>
            <a href="#for-who" className={styles.navLink}>
              {t("landing_nav_who_its_for")}
            </a>
            <a href="#telegram" className={styles.navLink}>
              {t("landing_nav_telegram")}
            </a>
            <a href="#features" className={styles.navLink}>
              {t("landing_nav_features")}
            </a>
            <a href="#plans" className={styles.navLink}>
              {t("landing_nav_pricing")}
            </a>
            <a href="#contact" className={styles.navLink}>
              {t("landing_nav_contact")}
            </a>
          </nav>

          <div className={styles.actions}>
            <a href="#contact" className={styles.secondaryAction}>
              {t("landing_contact_us")}
            </a>
            <a href="#plans" className={styles.primaryAction}>
              {t("landing_view_pricing")}
            </a>
            <Link to={DEMO_PATH} className={styles.demoAction}>
              {t("landing_try_demo")}
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className={styles.hero}>
          <span className={styles.heroGlowOne} />
          <span className={styles.heroGlowTwo} />
          <span className={styles.heroGlowThree} />

          <div className={styles.heroInner}>
            <div className={styles.heroContent}>
              <div className={styles.kickerWrap}>
                <span className={styles.kicker}>{t("landing_currently_in_beta")}</span>
                <span className={styles.kickerLine} />
              </div>

              <AnimatedHeroTitle />

              <p className={styles.heroText}>
                {t("landing_hero_text", { companyName })}
              </p>

              <div className={styles.heroActions}>
                <a href="#contact" className={styles.heroPrimary}>
                  {t("landing_send_request")}
                </a>
                <a href="#telegram" className={styles.heroSecondary}>
                  {t("landing_watch_how_it_works")}
                </a>
                <Link to={DEMO_PATH} className={styles.heroDemo}>
                  {t("landing_try_demo")}
                </Link>
              </div>

              <div className={styles.trustRow}>
                {trustItems.map((item) => (
                  <div key={item} className={styles.trustItem}>
                    <span className={styles.trustDot} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.heroFlow} aria-label={t("landing_telegram_title")}>
              <div className={styles.flowTelegramBadge} aria-hidden="true"><i class="lni lni-telegram"></i></div>
              <div className={styles.flowConnectorTop} aria-hidden="true" />

              {[
                { icon: <i class="lni lni-message-2"></i>, title: "1. Telegram request", text: "Client writes to your bot", tone: "violet" },
                { icon: <i class="lni lni-user-4"></i>, title: "2. New contact", text: "Saved in CRM", tone: "blue" },
                { icon: <i class="lni lni-file-plus-circle"></i>, title: "3. Lead created", text: "Deal in your pipeline", tone: "orange" },
                {
                  icon: <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M6.38992 12.3718C5.77921 12.8671 4.8865 12.7925 4.36647 12.2027C3.89531 11.6683 3.87645 10.8726 4.32176 10.3165L7.66638 6.13975C8.01035 5.7102 8.53085 5.46019 9.08115 5.46019H14.3429C14.5289 5.46019 14.7082 5.39109 14.8461 5.26632L15.1971 4.94874C15.1273 4.63721 15.2631 4.30349 15.5542 4.13543L17.783 2.84865C18.1417 2.64155 18.6004 2.76445 18.8075 3.12317L21.4013 7.61585C21.6085 7.97457 21.4855 8.43326 21.1268 8.64037L18.8981 9.92714C18.682 10.0519 18.4296 10.0569 18.2178 9.96272L16.8956 11.0197C16.3433 11.4612 15.6404 11.7706 14.8614 11.7652C14.1589 11.7603 13.1102 11.6865 12.1481 11.339H9.9189C9.35162 11.339 8.8513 11.0523 8.55494 10.6159L6.38992 12.3718ZM15.8525 6.37859L15.9384 6.30088L17.3461 8.73912L15.959 9.84802C15.6165 10.1218 15.2384 10.2678 14.8719 10.2652C14.215 10.2606 13.3328 10.1861 12.5942 9.90461C12.4841 9.86268 12.3639 9.83897 12.2388 9.83897H9.9189C9.8374 9.83897 9.77134 9.77291 9.77134 9.69141C9.77134 9.6608 9.78066 9.63237 9.79662 9.6088L9.8497 9.56575L9.86276 9.55491C9.88007 9.54778 9.89902 9.54386 9.9189 9.54386H12.2845C12.6987 9.54386 13.0345 9.20807 13.0345 8.79386C13.0345 8.37964 12.6987 8.04386 12.2845 8.04386H9.9189C9.47342 8.04386 9.06924 8.22066 8.77269 8.50792L5.68881 11.0091L8.83724 7.07734C8.89654 7.00329 8.98628 6.96019 9.08115 6.96019H14.3429C14.9008 6.96019 15.4388 6.75291 15.8525 6.37859ZM19.7273 7.71633L17.8835 4.52269L16.9537 5.05946L18.7976 8.2531L19.7273 7.71633Z" fill="#fff"></path>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M17.612 12.1284C18.2227 11.633 19.1155 11.7076 19.6355 12.2975C20.1066 12.8318 20.1255 13.6276 19.6802 14.1837L16.3356 18.3604C15.9916 18.79 15.4711 19.04 14.9208 19.04H9.65902C9.47305 19.04 9.29372 19.1091 9.15582 19.2338L8.80483 19.5514C8.87463 19.863 8.73882 20.1967 8.44773 20.3647L6.21897 21.6515C5.86025 21.8586 5.40156 21.7357 5.19445 21.377L2.60061 16.8843C2.3935 16.5256 2.51641 16.0669 2.87513 15.8598L5.10388 14.573C5.31999 14.4483 5.57239 14.4433 5.7842 14.5374L7.10638 13.4805C7.65867 13.039 8.36153 12.7295 9.14051 12.735C9.84302 12.7399 10.8917 12.8137 11.8538 13.1612H14.0831C14.6503 13.1612 15.1507 13.4479 15.447 13.8843L17.612 12.1284ZM8.14941 18.1216L8.06353 18.1993L6.65582 15.7611L8.04299 14.6521C8.38544 14.3784 8.76354 14.2324 9.13002 14.2349C9.78693 14.2395 10.6692 14.3141 11.4078 14.5956C11.5178 14.6375 11.638 14.6612 11.7631 14.6612H14.0831C14.1645 14.6612 14.2306 14.7273 14.2306 14.8088C14.2306 14.8394 14.2213 14.8678 14.2053 14.8914L14.1523 14.9344L14.1392 14.9453C14.1219 14.9524 14.1029 14.9563 14.0831 14.9563H11.7174C11.3032 14.9563 10.9674 15.2921 10.9674 15.7063C10.9674 16.1205 11.3032 16.4563 11.7174 16.4563H14.0831C14.5285 16.4563 14.9327 16.2795 15.2293 15.9922L18.3131 13.4911L15.1647 17.4228C15.1054 17.4969 15.0157 17.54 14.9208 17.54H9.65902C9.10111 17.54 8.56311 17.7473 8.14941 18.1216ZM4.27464 16.7838L6.11849 19.9775L7.04821 19.4407L5.20436 16.2471L4.27464 16.7838Z" fill="#fff"></path>
                  </svg>, title: "4. Manager task", text: "Ready for follow-up", tone: "green"
                },
              ].map((step) => (
                <div key={step.title} className={styles.flowStep}>
                  <span className={`${styles.flowStepIcon} ${styles[`flowStepIcon${step.tone}`]}`}>
                    {step.icon}
                  </span>
                  <span className={styles.flowStepCopy}>
                    <strong>{step.title}</strong>
                    <small>{step.text}</small>
                  </span>
                </div>
              ))}
            </div>

            <div className={styles.heroVisual}>
              <div className={styles.visualOrbit} />
              <div className={styles.visualOrbitTwo} />

              <div className={styles.dashboardShell}>
                <div className={styles.dashboardTop}>
                  <div className={styles.dashboardTitleGroup}>
                    <span className={styles.dashboardEyebrow}>
                      {t("landing_live_workspace")}
                    </span>
                    <strong className={styles.dashboardTitle}>
                      {t("landing_sales_command_center")}
                    </strong>
                  </div>
                  <div className={styles.liveBadge}>{t("landing_beta_badge")}</div>
                </div>

                <div className={styles.botPreviewCard}>
                  <div className={styles.botHeaderLine}>
                    <span className={styles.telegramIcon} aria-hidden="true"><i class="lni lni-telegram"></i></span>
                    <div>
                      <strong>{t("landing_bot_preview_title")}</strong>
                      <span>{t("landing_bot_preview_subtitle")}</span>
                    </div>
                  </div>

                  <div className={styles.chatBubbleLeft}>
                    {t("landing_bot_message_question")}
                  </div>
                  <div className={styles.chatAnswerRow}>
                    <div className={styles.chatBubbleRight}>
                      {t("landing_bot_message_answer")}
                    </div>
                    <span className={styles.clientAvatar} aria-hidden="true"><img src={faceLogo} alt={t("landing_logo_alt")} style={{ size: '30px', 'border-radius': '25px', width: '40px', height: '40px', objectFit: 'cover' }} /></span>
                  </div>

                  <div className={styles.crmResultCard}>
                    <div className={styles.crmResultTop}>
                      <span>{t("landing_crm_result_badge")}</span>
                      <strong>{t("landing_crm_result_title")}</strong>
                    </div>
                    <div className={styles.crmResultGrid}>
                      <span>{t("landing_crm_result_contact")}</span>
                      <span>{t("landing_crm_result_lead")}</span>
                      <span>{t("landing_crm_result_task")}</span>
                      <span>{t("landing_crm_result_history")}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.floatCardLeft}>
                <span>{t("landing_float_card_1_label")}</span>
                <strong>{t("landing_float_card_1_value")}</strong>
              </div>
              <div className={styles.floatCardRight}>
                <span>{t("landing_float_card_2_label")}</span>
                <strong>{t("landing_float_card_2_value")}</strong>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.statsSection}>
          <div className={styles.statsInner}>
            {[
              {
                icon: <svg width="40" height="40" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
                  <path opacity="0.4" d="M16.25 6.5H14.75H9.25H7.75H4.75C3.50736 6.5 2.5 7.50736 2.5 8.75V17.75C2.5 18.9926 3.50736 20 4.75 20H19.2502C20.4928 20 21.5002 18.9926 21.5002 17.75V8.75C21.5002 7.50736 20.4928 6.5 19.2502 6.5H16.25Z" fill="#343C54"></path>
                  <path d="M10 3.5C8.75736 3.5 7.75 4.50736 7.75 5.75V6.5H9.25V5.75C9.25 5.33579 9.58579 5 10 5H14C14.4142 5 14.75 5.33579 14.75 5.75V6.5H16.25V5.75C16.25 4.50736 15.2426 3.5 14 3.5H10Z" fill="#343C54"></path>
                </svg>, title: "How it works", text: "From Telegram to closed deal in 4 simple steps", tone: "blue"
              },
              {
                icon: <svg width="40" height="40" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
                  <path d="M21.125 2.90383C21.125 2.60174 20.9438 2.32914 20.6652 2.21225C20.3867 2.09536 20.0652 2.157 19.8496 2.36863C17.6977 4.4811 15.2616 5.61469 13.354 6.21977C12.4015 6.52189 11.5862 6.69048 11.0129 6.78324L10.9238 6.79735L10.9238 15.0538C12.587 15.3121 16.5504 16.2449 19.8496 19.4837C20.0652 19.6954 20.3867 19.757 20.6652 19.6401C20.9438 19.5232 21.125 19.2506 21.125 18.9485V13.548C21.9989 13.2391 22.625 12.4057 22.625 11.426V10.426C22.625 9.44638 21.9989 8.61295 21.125 8.30408V2.90383Z" fill="#343C54"></path>
                  <path d="M5.125 6.89016H9.42383L9.42383 14.9608H5.125C3.88236 14.9608 2.875 13.9535 2.875 12.7108V9.14016C2.875 7.89752 3.88236 6.89016 5.125 6.89016Z" fill="#343C54"></path>
                  <path opacity="0.4" d="M10.1131 15.9609H6.5918L6.93925 18.4119C7.09654 19.5214 8.04635 20.346 9.16697 20.346H9.32729C10.7163 20.346 11.7736 19.0998 11.5472 17.7294L11.2834 16.132C10.6689 16.0124 10.2486 15.9718 10.1131 15.9609Z" fill="#343C54"></path>
                </svg>, title: "For your business", text: "Perfect for service businesses and local teams", tone: "blue"
              },
              {
                icon: <svg width="40" height="40" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
                  <path d="M7.5 18.5C7.5 19.4665 6.7165 20.25 5.75 20.25H3.75C2.7835 20.25 2 19.4665 2 18.5V16.5C2 15.5335 2.7835 14.75 3.75 14.75H5.75C6.7165 14.75 7.5 15.5335 7.5 16.5V18.5ZM14.75 18.5C14.75 19.4665 13.9665 20.25 13 20.25H11C10.0335 20.25 9.25 19.4665 9.25 18.5V16.5C9.25 15.5335 10.0335 14.75 11 14.75H13C13.9665 14.75 14.75 15.5335 14.75 16.5V18.5ZM22 18.5C22 19.4665 21.2165 20.25 20.25 20.25H18.25C17.2835 20.25 16.5 19.4665 16.5 18.5V16.5C16.5 15.5335 17.2835 14.75 18.25 14.75H20.25C21.2165 14.75 22 15.5335 22 16.5V18.5ZM14.75 8.5C14.75 9.4665 13.9665 10.25 13 10.25H11C10.0335 10.25 9.25 9.4665 9.25 8.5V6.5C9.25 5.5335 10.0335 4.75 11 4.75H13C13.9665 4.75 14.75 5.5335 14.75 6.5V8.5Z" fill="#343C54"></path>
                  <path opacity="0.4" d="M12.75 11.75H17.75C18.9926 11.75 20 12.7574 20 14V14.75H18.5V14C18.5 13.5858 18.1642 13.25 17.75 13.25H12.75V14.75H11.25V13.25H6.25C5.83579 13.25 5.5 13.5858 5.5 14V14.75H4V14C4 12.7574 5.00736 11.75 6.25 11.75H11.25V10.25H12.75V11.75Z" fill="#343C54"></path>
                </svg>, title: "Powerful features", text: "Everything you need to grow and scale", tone: "violet"
              },
              {
                icon: <svg width="40" height="40" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
                  <path d="M18.75 5.75C18.75 5.33579 18.4142 5 18 5C17.5858 5 17.25 5.33579 17.25 5.75V5.98626C16.4901 6.2639 15.9477 6.99321 15.9477 7.84921C15.9477 8.67571 16.4604 9.41552 17.2342 9.70575L18.2391 10.0826C18.4275 10.1533 18.5523 10.3334 18.5523 10.5346C18.5523 10.8013 18.3362 11.0174 18.0695 11.0174H17.6745C17.5492 11.0174 17.4477 10.9159 17.4477 10.7906C17.4477 10.3764 17.1119 10.0406 16.6977 10.0406C16.2835 10.0406 15.9477 10.3764 15.9477 10.7906C15.9477 11.5978 16.5016 12.2757 17.25 12.4649V12.6337C17.25 13.0479 17.5858 13.3837 18 13.3837C18.4142 13.3837 18.75 13.0479 18.75 12.6337V12.3976C19.5099 12.12 20.0523 11.3906 20.0523 10.5346C20.0523 9.70814 19.5396 8.96833 18.7658 8.67811L17.7609 8.30127C17.5725 8.2306 17.4477 8.05046 17.4477 7.84921C17.4477 7.58257 17.6638 7.36641 17.9305 7.36641H18.3255C18.4508 7.36641 18.5523 7.46796 18.5523 7.59323C18.5523 8.00744 18.8881 8.34323 19.3023 8.34323C19.7165 8.34323 20.0523 8.00744 20.0523 7.59323C20.0523 6.78601 19.4984 6.10815 18.75 5.91898V5.75Z" fill="#343C54"></path>
                  <path d="M4.38379 7.05326C4.38379 6.16061 5.10742 5.43698 6.00007 5.43698C6.89271 5.43698 7.61635 6.16061 7.61635 7.05326C7.61635 7.9459 6.89271 8.66954 6.00007 8.66954C5.10742 8.66954 4.38379 7.9459 4.38379 7.05326Z" fill="#343C54"></path>
                  <path d="M2.54688 11.5181C2.54688 10.2819 3.54903 9.27975 4.78525 9.27975H7.21356C8.44977 9.27975 9.45193 10.2819 9.45193 11.5181V12.4484C9.45193 12.8626 9.11614 13.1984 8.70193 13.1984H3.29688C2.88266 13.1984 2.54688 12.8626 2.54688 12.4484V11.5181Z" fill="#343C54"></path>
                  <path opacity="0.4" d="M2.75 15.5005C2.33579 15.5005 2 15.8363 2 16.2505C2 16.6647 2.33579 17.0005 2.75 17.0005H11.0394L10.5224 19.0686C10.4664 19.2926 10.5167 19.53 10.6588 19.712C10.801 19.8941 11.019 20.0005 11.25 20.0005H12.75C12.981 20.0005 13.199 19.8941 13.3412 19.712C13.4833 19.53 13.5336 19.2926 13.4776 19.0686L12.9606 17.0005H21.25C21.6642 17.0005 22 16.6647 22 16.2505C22 15.8363 21.6642 15.5005 21.25 15.5005H2.75Z" fill="#343C54"></path>
                </svg>, title: "Affordable pricing", text: "Transparent plans with fair pricing", tone: "violet"
              },
            ].map((item) => (
              <article key={item.title} className={styles.statCard}>
                <span className={`${styles.statIconBox} ${styles[`statIconBox${item.tone}`]}`}>{item.icon}</span>
                <span className={styles.statCardCopy}>
                  <strong>{item.title}</strong>
                  <small>{item.text}</small>
                </span>
              </article>
            ))}
          </div>
        </section>

        <section id="telegram" className={styles.telegramSection}>
          <div className={styles.telegramInner}>
            <div className={styles.telegramContent}>
              <span className={styles.sectionKicker}>{t("landing_telegram_kicker")}</span>
              <h2 className={styles.sectionTitle}>
                {t("landing_telegram_title")}
              </h2>
              <p className={styles.sectionText}>
                {t("landing_telegram_text", { companyName })}
              </p>

              <div className={styles.telegramList}>
                {telegramItems.map((item) => (
                  <div key={item} className={styles.telegramListItem}>
                    <span>✓</span>
                    <strong>{item}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.workflowPanel}>
              {workflowItems.map((item) => (
                <article key={item.number} className={styles.workflowItem}>
                  <span>{item.number}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="for-who" className={styles.featuresSection}>
          <div className={styles.sectionIntro}>
            <span className={styles.sectionKicker}>{t("landing_nav_who_its_for")}</span>
            <h2 className={styles.sectionTitle}>
              {t("landing_for_who_title")}
            </h2>
            <p className={styles.sectionText}>
              {t("landing_for_who_text", { companyName })}
            </p>
          </div>

          <div className={styles.fitGrid}>
            {fitItems.map((item) => (
              <article key={item.title} className={styles.featureCard}>
                <span className={styles.featureEyebrow}>{t("landing_best_fit")}</span>
                <h3 className={styles.featureTitle}>{item.title}</h3>
                <p className={styles.featureText}>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="features" className={styles.featuresSection}>
          <div className={styles.sectionIntro}>
            <span className={styles.sectionKicker}>{t("landing_why_it_works")}</span>
            <h2 className={styles.sectionTitle}>
              {t("landing_features_title")}
            </h2>
            <p className={styles.sectionText}>
              {t("landing_features_text")}
            </p>
          </div>

          <SectionCards items={featureItems} />
        </section>

        <section id="plans" className={styles.pricingSection}>
          <div className={styles.sectionIntro}>
            <span className={styles.sectionKicker}>{t("landing_nav_pricing")}</span>
            <h2 className={styles.sectionTitle}>
              {t("landing_pricing_title")}
            </h2>
            <p className={styles.sectionText}>
              {t("landing_pricing_text")}
            </p>
          </div>

          <div className={styles.planGrid}>
            {plans.map((plan) => (
              <article
                key={plan.name}
                className={`${styles.planCard} ${plan.tone === "primary"
                    ? styles.planCardPrimary
                    : plan.tone === "dark"
                      ? styles.planCardDark
                      : styles.planCardLight
                  }`}
              >
                {plan.badge ? <div className={styles.planBadge}>{plan.badge}</div> : null}

                <div>
                  <div className={styles.planHeader}>
                    <h3 className={styles.planName}>{plan.name}</h3>
                    <div className={styles.planPriceRow}>
                      <span className={styles.planPrice}>{plan.price}</span>
                      <span className={styles.planPeriod}>{plan.period}</span>
                    </div>
                    <p className={styles.planDescription}>{plan.description}</p>
                  </div>

                  <ul className={styles.planList}>
                    {plan.points.map((point) => (
                      <li key={point} className={styles.planItem}>
                        <span className={styles.planCheck}>•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={styles.planFooter}>
                  <div className={styles.planSetup}>{plan.setup}</div>
                  <a href="#contact" className={styles.planMainButton}>
                    {t("landing_make_an_order")}
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.finalCtaSection}>
          <div className={styles.finalCtaCard}>
            <span className={styles.finalCtaGlow} />
            <div className={styles.finalCtaContent}>
              <span className={styles.sectionKicker}>{t("landing_final_cta_kicker")}</span>
              <h2 className={styles.finalCtaTitle}>{t("landing_final_cta_title")}</h2>
              <p className={styles.finalCtaText}>{t("landing_final_cta_text")}</p>
              <div className={styles.finalCtaActions}>
                <a href="#contact" className={styles.heroPrimary}>{t("landing_send_request")}</a>
                <Link to={DEMO_PATH} className={styles.heroDemo}>{t("landing_try_demo")}</Link>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className={styles.contactSection}>
          <div className={styles.contactCard}>
            <div className={styles.contactIntro}>
              <span className={styles.sectionKicker}>{t("landing_contact_form")}</span>
              <h2 className={styles.contactTitle}>{t("landing_contact_title")}</h2>
              <p className={styles.contactText}>
                {t("landing_contact_text")}
              </p>
            </div>

            {!success ? (
              <form onSubmit={handleSubmit} className={styles.inlineForm}>
                <div className={styles.formField}>
                  <label htmlFor="email">{t("email")}</label>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder={t("landing_email_placeholder")}
                  />
                </div>

                <div className={styles.formField}>
                  <label htmlFor="message">{t("landing_message_label")}</label>
                  <textarea
                    id="message"
                    rows={6}
                    value={form.message}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        message: e.target.value,
                      }))
                    }
                    placeholder={t("landing_message_placeholder")}
                  />
                </div>

                <div className={styles.captchaWrap}>
                  <TurnstileWidget
                    onVerify={(token) =>
                      setForm((prev) => ({
                        ...prev,
                        captcha_token: token,
                      }))
                    }
                    onExpire={() =>
                      setForm((prev) => ({
                        ...prev,
                        captcha_token: "",
                      }))
                    }
                    onError={() => {
                      setForm((prev) => ({
                        ...prev,
                        captcha_token: "",
                      }));
                      setError(t("landing_form_error_captcha_load"));
                    }}
                  />
                </div>

                {error ? <div className={styles.formError}>{error}</div> : null}

                <div className={styles.inlineFormActions}>
                  <button
                    type="submit"
                    className={styles.formMainButton}
                    disabled={submitting}
                  >
                    {submitting ? t("sending") : t("landing_send_request")}
                  </button>
                </div>
              </form>
            ) : (
              <div className={styles.successStateInline}>
                <span className={styles.sectionKicker}>{t("landing_sent")}</span>
                <h3 className={styles.successInlineTitle}>
                  {t("landing_success_title")}
                </h3>
                <p className={styles.successInlineText}>
                  {t("landing_success_text")}
                </p>
                <button
                  type="button"
                  className={styles.formGhostButton}
                  onClick={() => {
                    setSuccess(false);
                    setError("");
                  }}
                >
                  {t("landing_send_another_request")}
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <img src={logoSymbol} alt={t("landing_logo_alt")} className={styles.logoIcon} />
          </div>

          <div className={styles.footerLinks}>
            <a href="#for-who">{t("landing_nav_who_its_for")}</a>
            <a href="#telegram">{t("landing_nav_telegram")}</a>
            <a href="#features">{t("landing_nav_features")}</a>
            <a href="#plans">{t("landing_nav_pricing")}</a>
            <a href="#contact">{t("landing_nav_contact")}</a>
            <Link to="/login">{t("login")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
